import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ExternalLink, Shield, AlertTriangle, Code2, PlayCircle } from 'lucide-react';
import { Sidebar } from '../../components/apidocs/Sidebar';
import { CodePanel, ResponseBlock } from '../../components/apidocs/CodePanel';
import { ApiTester } from '../../components/apidocs/ApiTester';
import { DOCS, SECTIONS } from '../../data/apiDocsData';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const METHOD_STYLES = {
  GET:    { pill: 'bg-sky-100 text-sky-700 border border-sky-200',    dot: 'bg-sky-500' },
  POST:   { pill: 'bg-emerald-100 text-emerald-700 border border-emerald-200', dot: 'bg-emerald-500' },
  PUT:    { pill: 'bg-amber-100 text-amber-700 border border-amber-200', dot: 'bg-amber-500' },
  DELETE: { pill: 'bg-red-100 text-red-700 border border-red-200',    dot: 'bg-red-500' },
};

const IN_LABELS = { body: 'Body', path: 'Path', query: 'Query', header: 'Header', info: 'Info' };
const IN_COLORS = {
  body:   'bg-slate-100 text-slate-600',
  path:   'bg-purple-100 text-purple-700',
  query:  'bg-sky-100 text-sky-700',
  header: 'bg-amber-100 text-amber-700',
  info:   'bg-slate-100 text-slate-500',
};

function MethodBadge({ method }) {
  if (!method) return null;
  const s = METHOD_STYLES[method] || { pill: 'bg-slate-100 text-slate-600 border border-slate-200' };
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-bold tracking-wide font-mono ${s.pill}`}>
      {method}
    </span>
  );
}

function ParamTable({ params }) {
  if (!params || params.length === 0) return null;
  return (
    <>
      {/* Mobile: card list */}
      <div className="sm:hidden space-y-2">
        {params.map((p, i) => (
          <div key={i} className="border border-slate-200 rounded-xl p-3 bg-white">
            <div className="flex items-center gap-2 flex-wrap mb-1.5">
              <code className="text-xs font-mono font-semibold text-slate-800">{p.name}</code>
              {p.required && (
                <span className="text-[9px] font-bold text-red-500 uppercase tracking-wide">required</span>
              )}
              <span className={`px-1.5 py-0.5 rounded text-[9px] font-semibold ${IN_COLORS[p.in] || IN_COLORS.body}`}>
                {IN_LABELS[p.in] || p.in}
              </span>
              <code className="text-[10px] text-slate-400 font-mono">{p.type}</code>
            </div>
            <p className="text-xs text-slate-500 leading-relaxed">{p.description}</p>
          </div>
        ))}
      </div>

      {/* Desktop: table */}
      <div className="hidden sm:block overflow-x-auto rounded-xl border border-slate-200">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide w-36">Parameter</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide w-20">In</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide w-20">Type</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Description</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {params.map((p, i) => (
              <tr key={i} className="hover:bg-slate-50/50">
                <td className="px-4 py-3 align-top">
                  <code className="text-xs font-mono font-semibold text-slate-800">{p.name}</code>
                  {p.required && (
                    <span className="ml-1.5 text-[10px] font-bold text-red-500 uppercase">req</span>
                  )}
                </td>
                <td className="px-4 py-3 align-top">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${IN_COLORS[p.in] || IN_COLORS.body}`}>
                    {IN_LABELS[p.in] || p.in}
                  </span>
                </td>
                <td className="px-4 py-3 align-top">
                  <code className="text-[11px] text-slate-500 font-mono">{p.type}</code>
                </td>
                <td className="px-4 py-3 text-slate-600 text-xs leading-relaxed align-top">{p.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ErrorTable({ rows }) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide w-20">Status</th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide w-36">Label</th>
            <th className="text-left px-4 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wide">Description</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r, i) => (
            <tr key={i}>
              <td className="px-4 py-2.5">
                <code className={`text-xs font-bold ${r.code.startsWith('2') ? 'text-emerald-600' : r.code.startsWith('4') ? 'text-amber-600' : 'text-red-600'}`}>
                  {r.code}
                </code>
              </td>
              <td className="px-4 py-2.5 text-xs font-medium text-slate-700">{r.label}</td>
              <td className="px-4 py-2.5 text-xs text-slate-500">{r.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Simple markdown bold parser
function Prose({ text }) {
  if (!text) return null;
  const lines = text.split('\n');
  return (
    <div className="space-y-2">
      {lines.map((line, i) => {
        if (!line.trim()) return <div key={i} className="h-1" />;
        const parts = line.split(/(\*\*[^*]+\*\*)/g);
        return (
          <p key={i} className="text-slate-600 text-sm leading-relaxed">
            {parts.map((p, j) =>
              p.startsWith('**') && p.endsWith('**')
                ? <strong key={j} className="font-semibold text-slate-800">{p.slice(2, -2)}</strong>
                : p
            )}
          </p>
        );
      })}
    </div>
  );
}

// ─── Endpoint View ─────────────────────────────────────────────────────────────

function EndpointView({ doc, lang, onLangChange }) {
  const [rightTab, setRightTab] = useState('code');
  const hasTester = !!doc?.method;

  // Reset to code tab when switching endpoints
  useEffect(() => { setRightTab('code'); }, [doc?.path]);

  if (!doc) return null;

  return (
    <div className="flex flex-col-reverse lg:flex-row gap-6 lg:gap-8 min-h-0">
      {/* Left: docs — second on mobile, first on desktop */}
      <div className="flex-1 min-w-0 space-y-6">
        {/* Title + method + path */}
        <div>
          <div className="flex items-center gap-3 flex-wrap mb-3">
            <MethodBadge method={doc.method} />
            {doc.badge && (
              <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-bold bg-blue-100 text-blue-600 rounded-full">
                <Shield className="w-3 h-3" /> {doc.badge}
              </span>
            )}
          </div>
          {doc.path && (
            <div className="font-mono text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 mb-4 overflow-x-auto">
              {doc.path}
            </div>
          )}
          <Prose text={doc.description} />
        </div>

        {/* Note */}
        {doc.note && (
          <div className="flex items-start gap-3 p-3.5 bg-amber-50 border border-amber-200 rounded-xl">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800 leading-relaxed">{doc.note}</p>
          </div>
        )}

        {/* Parameters */}
        {doc.params && doc.params.length > 0 && (
          <div>
            <h3 className="text-sm font-semibold text-slate-800 mb-3">Parameters</h3>
            <ParamTable params={doc.params} />
          </div>
        )}

        {/* Error table */}
        {doc.errorTable && (
          <div>
            <h3 className="text-sm font-semibold text-slate-800 mb-3">HTTP Status Codes</h3>
            <ErrorTable rows={doc.errorTable} />
          </div>
        )}
      </div>

      {/* Right: code + tester — first on mobile, right column on desktop */}
      <div className="w-full lg:w-[440px] xl:w-[480px] flex-shrink-0">
        {doc.examples && (
          <div className="lg:sticky lg:top-24 space-y-3">

            {/* Tab toggle — only when endpoint has a method */}
            {hasTester && (
              <div className="flex items-center gap-1 bg-slate-900 rounded-xl p-1" data-testid="right-panel-tabs">
                <button
                  onClick={() => setRightTab('code')}
                  data-testid="tab-code"
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all ${
                    rightTab === 'code'
                      ? 'bg-slate-700 text-white shadow-sm'
                      : 'text-slate-500 hover:text-slate-300'
                  }`}
                >
                  <Code2 className="w-3.5 h-3.5" />
                  Code
                </button>
                <button
                  onClick={() => setRightTab('try')}
                  data-testid="tab-try-it"
                  className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all ${
                    rightTab === 'try'
                      ? 'bg-blue-600 text-white shadow-sm shadow-blue-900/40'
                      : 'text-slate-500 hover:text-slate-300'
                  }`}
                >
                  <PlayCircle className="w-3.5 h-3.5" />
                  Try it
                </button>
              </div>
            )}

            {/* Code panel */}
            {rightTab === 'code' && (
              <>
                <CodePanel examples={doc.examples} sharedLang={lang} onLangChange={onLangChange} />
                {doc.response && (
                  <div className="mt-1">
                    <ResponseBlock response={doc.response} />
                  </div>
                )}
              </>
            )}

            {/* Interactive tester */}
            {rightTab === 'try' && hasTester && (
              <ApiTester doc={doc} />
            )}

          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function APIDocs() {
  const [activeId, setActiveId] = useState('authentication');
  const [lang, setLang] = useState('curl');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Deep-linking via URL hash
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && DOCS[hash]) setActiveId(hash);
  }, []);

  const handleSelect = (id) => {
    setActiveId(id);
    setSidebarOpen(false);
    window.history.replaceState(null, '', `#${id}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const doc = DOCS[activeId];

  // Breadcrumb
  let sectionLabel = '';
  for (const sec of SECTIONS) {
    if (sec.endpoints.find(ep => ep.id === activeId)) { sectionLabel = sec.label; break; }
  }

  return (
    <div className="min-h-screen bg-white flex flex-col" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* ── Top bar ─────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-30 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-6 h-14 flex-shrink-0">
        <div className="flex items-center gap-3">
          {/* Mobile sidebar toggle */}
          <button
            className="lg:hidden p-1.5 text-slate-500 hover:text-slate-800"
            onClick={() => setSidebarOpen(o => !o)}
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-blue-600 rounded-lg flex items-center justify-center">
              <span className="text-white text-xs font-black">N</span>
            </div>
            <span className="font-bold text-slate-900 text-sm">Nanokart</span>
          </Link>
          <span className="hidden sm:block text-slate-300">/</span>
          <span className="hidden sm:block text-sm font-medium text-slate-500">api.nanokart.ai</span>
          <span className="hidden sm:block text-slate-300">/</span>
          <span className="hidden sm:block text-sm font-semibold text-slate-800">API Reference</span>
        </div>

        <div className="flex items-center gap-3">
          <Link
            to="/docs"
            className="hidden sm:flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 transition-colors"
          >
            v1.0
          </Link>
          <Link
            to="/partners/login"
            className="flex items-center gap-1.5 px-3.5 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg transition-colors"
          >
            Get API Key
            <ExternalLink className="w-3 h-3" />
          </Link>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        {/* ── Sidebar (desktop) ──────────────────────────────────────────── */}
        <div className="hidden lg:flex w-64 xl:w-72 flex-shrink-0 h-[calc(100vh-3.5rem)] sticky top-14">
          <Sidebar activeId={activeId} onSelect={handleSelect} />
        </div>

        {/* ── Mobile sidebar drawer ──────────────────────────────────────── */}
        {sidebarOpen && (
          <div className="lg:hidden fixed inset-0 z-20 flex" onClick={() => setSidebarOpen(false)}>
            <div className="w-72 max-w-[80vw] h-full bg-white shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="h-14 flex items-center justify-between px-4 border-b border-slate-200">
                <span className="font-semibold text-sm text-slate-700">Navigation</span>
                <button onClick={() => setSidebarOpen(false)} className="p-1.5 text-slate-400 hover:text-slate-700">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                <Sidebar activeId={activeId} onSelect={(id) => { handleSelect(id); setSidebarOpen(false); }} />
              </div>
            </div>
            <div className="flex-1 bg-black/40 backdrop-blur-sm" />
          </div>
        )}

        {/* ── Main content ───────────────────────────────────────────────── */}
        <main className="flex-1 overflow-y-auto min-w-0">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-xs text-slate-400 mb-4 sm:mb-6">
              <span className="hidden sm:inline">{sectionLabel}</span>
              {doc?.title && (
                <>
                  <span className="hidden sm:inline">/</span>
                  <span className="text-slate-600 font-medium">{doc.title}</span>
                </>
              )}
              {doc?.title && (
                <span className="sm:hidden text-slate-600 font-medium">{sectionLabel} / {doc.title}</span>
              )}
            </div>

            {/* Page title */}
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-6 sm:mb-8">
              {doc?.title || 'Documentation'}
            </h1>

            {/* Endpoint content */}
            {doc && (
              <EndpointView
                doc={doc}
                lang={lang}
                onLangChange={setLang}
              />
            )}

            {/* Footer nav */}
            <div className="mt-12 sm:mt-16 pt-6 sm:pt-8 border-t border-slate-100 flex items-center justify-between text-sm">
              <PrevNextNav activeId={activeId} onSelect={handleSelect} />
            </div>
          </div>
        </main>
      </div>
    </div>
  );
}

// ─── Prev / Next navigation ────────────────────────────────────────────────────
function PrevNextNav({ activeId, onSelect }) {
  const allEndpoints = SECTIONS.flatMap(s => s.endpoints);
  const idx = allEndpoints.findIndex(ep => ep.id === activeId);
  const prev = idx > 0 ? allEndpoints[idx - 1] : null;
  const next = idx < allEndpoints.length - 1 ? allEndpoints[idx + 1] : null;

  return (
    <div className="w-full flex items-center justify-between gap-4">
      {prev ? (
        <button
          onClick={() => onSelect(prev.id)}
          className="flex flex-col items-start text-slate-400 hover:text-blue-600 transition-colors min-w-0 flex-1"
        >
          <span className="text-xs uppercase tracking-wide mb-1">← Previous</span>
          <span className="font-semibold text-slate-700 hover:text-blue-600 truncate max-w-full text-sm">{prev.label}</span>
        </button>
      ) : <div className="flex-1" />}
      {next && (
        <button
          onClick={() => onSelect(next.id)}
          className="flex flex-col items-end text-slate-400 hover:text-blue-600 transition-colors min-w-0 flex-1"
        >
          <span className="text-xs uppercase tracking-wide mb-1">Next →</span>
          <span className="font-semibold text-slate-700 hover:text-blue-600 truncate max-w-full text-sm">{next.label}</span>
        </button>
      )}
    </div>
  );
}
